// Generations submitted from the video agent, tracked for the whole SPA
// session.
//
// The poll loop lives here, not in the chat component: one loop per job,
// started the first time anything asks about it and kept running while the
// user browses other pages, until the job is done. The job card and the nav
// indicator just subscribe. A fresh page load recovers from the server: the
// conversation comes back with a snapshot of each of its jobs, and
// GET /agent/jobs/active lists what is still rendering in any chat.
//
// Polls read /api/v1/history/:id — a DB row that the server poller refreshes
// from the batch gateway every 30 s — so polling faster than that buys
// little: 5 s after a change, backing off to 20 s while nothing moves, 60 s
// in a background tab, and nothing once the job is terminal.

import { useEffect, useSyncExternalStore } from 'react';
import { listActiveJobs, type GenerationKind, type JobSnapshot, type JobStatus } from './agent';

export type JobResultUrl = { url: string; key: string };

export type TrackedJob = {
  activityId: number;
  kind: GenerationKind;
  conversationId?: string;
  title?: string;
  // null until the first status arrives.
  status: JobStatus | null;
  error: string | null;
  createdAt?: string;
  etaSeconds?: number;
  etaUpdatedAt?: string;
  queuePosition?: number;
  // Presigned outputs, once completed.
  results?: JobResultUrl[];
  // Last poll failed (network, 5xx); polling continues.
  pollError?: string;
  // Finished while this tab was watching and not shown since.
  unseen?: boolean;
};

export type JobRef = { activityId: number; kind: GenerationKind; conversationId?: string; title?: string };

type HistoryRow = {
  job_status: JobStatus;
  error_message: string | null;
  created_at?: string;
  metadata: {
    r2_key?: string;
    r2_keys?: string[];
    eta_seconds?: number;
    eta_updated_at?: string;
    queue_position?: number;
  } | null;
};

export const MIN_POLL_MS = 5_000;
export const MAX_POLL_MS = 20_000;
export const HIDDEN_POLL_MS = 60_000;
const ERROR_POLL_MS = 15_000;
// A poll that never answers would otherwise hold the job's loop forever.
export const POLL_TIMEOUT_MS = 30_000;

export const isTerminal = (s: JobStatus | null) => s === 'completed' || s === 'failed';
export const isActive = (j: TrackedJob) => !isTerminal(j.status);

function fromSnapshot(s: JobSnapshot): Partial<TrackedJob> {
  return {
    status: s.status,
    error: s.error,
    ...(s.created_at ? { createdAt: s.created_at } : {}),
    etaSeconds: s.eta_seconds,
    etaUpdatedAt: s.eta_updated_at,
    queuePosition: s.queue_position,
  };
}

function fromRow(r: HistoryRow): Partial<TrackedJob> {
  const md = r.metadata ?? {};
  return {
    status: r.job_status,
    error: r.error_message,
    ...(r.created_at ? { createdAt: r.created_at } : {}),
    etaSeconds: typeof md.eta_seconds === 'number' ? md.eta_seconds : undefined,
    etaUpdatedAt: md.eta_updated_at,
    queuePosition: r.job_status === 'pending' && typeof md.queue_position === 'number' && md.queue_position > 0 ? md.queue_position : undefined,
  };
}

function resultKeys(r: HistoryRow): string[] {
  const md = r.metadata;
  if (md?.r2_keys?.length) return md.r2_keys;
  return md?.r2_key ? [md.r2_key] : [];
}

export class AgentJobStore {
  private jobs = new Map<number, TrackedJob>();
  private list: TrackedJob[] = [];
  private listeners = new Set<() => void>();
  private timers = new Map<number, ReturnType<typeof setTimeout>>();
  private inflight = new Set<number>();
  private delays = new Map<number, number>();
  private bootstrapping: Promise<void> | null = null;
  private stopped = new Set<number>();

  constructor(
    private readonly fetchFn: typeof fetch = (...args) => fetch(...args),
    private readonly hidden: () => boolean = () => typeof document !== 'undefined' && document.hidden
  ) {
    if (typeof document !== 'undefined') {
      // Back in the tab: refresh everything still running right away.
      document.addEventListener('visibilitychange', () => {
        if (this.hidden()) return;
        for (const j of this.list) if (isActive(j) && this.timers.has(j.activityId)) this.schedule(j.activityId, 0);
      });
    }
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  get = (activityId: number): TrackedJob | undefined => this.jobs.get(activityId);

  all = (): TrackedJob[] => this.list;

  private set(job: TrackedJob) {
    this.jobs.set(job.activityId, job);
    this.list = [...this.jobs.values()];
    for (const fn of this.listeners) fn();
  }

  private patch(activityId: number, p: Partial<TrackedJob>) {
    const cur = this.jobs.get(activityId);
    if (cur) this.set({ ...cur, ...p });
  }

  // Start (or keep) tracking a job. A server snapshot seeds the status so
  // the card draws correctly before the first poll; it never overrides a
  // terminal status this tab has already seen.
  track(ref: JobRef, snapshot?: JobSnapshot) {
    const cur = this.jobs.get(ref.activityId);
    const next: TrackedJob = {
      ...(cur ?? { activityId: ref.activityId, kind: ref.kind, status: null, error: null }),
      ...(ref.conversationId ? { conversationId: ref.conversationId } : {}),
      ...(ref.title ? { title: ref.title } : {}),
      ...(snapshot && !isTerminal(cur?.status ?? null) ? fromSnapshot(snapshot) : {}),
    };
    const same = cur && (Object.keys(next) as (keyof TrackedJob)[]).every((k) => next[k] === cur[k]);
    if (!same) this.set(next);
    // The server still sees it running (e.g. a 401 while the session was
    // being refreshed stopped it): follow it again.
    if (snapshot && !isTerminal(snapshot.status)) this.stopped.delete(ref.activityId);
    this.ensurePolling(ref.activityId);
  }

  markSeen(activityId: number) {
    if (this.jobs.get(activityId)?.unseen) this.patch(activityId, { unseen: false });
  }

  // Pick up jobs submitted before this page load. Once per session.
  bootstrap(): Promise<void> {
    if (!this.bootstrapping) {
      this.bootstrapping = listActiveJobs()
        .then((items) => {
          for (const j of items) {
            this.track({ activityId: j.activity_id, kind: j.kind, conversationId: j.conversation_id, title: j.conversation_title }, j);
          }
        })
        .catch(() => {
          /* the chat still recovers its own jobs when opened */
        });
    }
    return this.bootstrapping;
  }

  private ensurePolling(activityId: number) {
    const job = this.jobs.get(activityId);
    if (!job || this.timers.has(activityId) || this.inflight.has(activityId) || this.stopped.has(activityId)) return;
    if (job.status === 'failed' || (job.status === 'completed' && job.results)) return;
    this.schedule(activityId, 0);
  }

  private schedule(activityId: number, ms: number) {
    const t = this.timers.get(activityId);
    if (t) clearTimeout(t);
    this.timers.set(
      activityId,
      setTimeout(() => void this.poll(activityId), ms)
    );
  }

  private nextDelay(activityId: number, changed: boolean): number {
    const prev = this.delays.get(activityId) ?? MIN_POLL_MS;
    const d = changed ? MIN_POLL_MS : Math.min(MAX_POLL_MS, Math.round(prev * 1.5));
    this.delays.set(activityId, d);
    return this.hidden() ? HIDDEN_POLL_MS : d;
  }

  private async poll(activityId: number) {
    this.timers.delete(activityId);
    const job = this.jobs.get(activityId);
    if (!job) return;
    this.inflight.add(activityId);
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), POLL_TIMEOUT_MS);
    try {
      const res = await this.fetchFn(`/api/v1/history/${activityId}`, { credentials: 'include', signal: abort.signal });
      if (res.status === 401 || res.status === 403 || res.status === 404) {
        // Deleted, or signed out: nothing more to learn.
        this.stopped.add(activityId);
        this.patch(activityId, { pollError: `HTTP ${res.status}` });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const row = (await res.json()) as HistoryRow;
      const before = this.jobs.get(activityId)!;
      const update = fromRow(row);
      const changed =
        update.status !== before.status || update.etaUpdatedAt !== before.etaUpdatedAt || update.queuePosition !== before.queuePosition;
      // Watched it finish (as opposed to loading an old, finished job).
      const unseen = isTerminal(row.job_status) && before.status !== null && !isTerminal(before.status) ? { unseen: true } : {};
      this.patch(activityId, { ...update, ...unseen, pollError: undefined });

      if (row.job_status === 'completed') {
        const keys = resultKeys(row);
        const p = await this.fetchFn(`/api/v1/history/${activityId}/presigned-url`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(keys.length ? { r2_keys: keys } : {}),
          signal: abort.signal,
        });
        if (!p.ok) throw new Error(`HTTP ${p.status}`);
        const data = (await p.json()) as { urls?: string[] };
        this.patch(activityId, { results: (data.urls ?? []).map((url, i) => ({ url, key: keys[i] ?? '' })) });
        return;
      }
      if (row.job_status === 'failed') return;
      this.schedule(activityId, this.nextDelay(activityId, changed));
    } catch (e) {
      this.patch(activityId, { pollError: e instanceof Error ? e.message : String(e) });
      this.schedule(activityId, this.hidden() ? HIDDEN_POLL_MS : ERROR_POLL_MS);
    } finally {
      clearTimeout(timeout);
      this.inflight.delete(activityId);
    }
  }
}

export const agentJobs = new AgentJobStore();

// The live state of one job; starts tracking it on first use.
export function useAgentJob(ref: JobRef | null): TrackedJob | undefined {
  const job = useSyncExternalStore(agentJobs.subscribe, () => (ref ? agentJobs.get(ref.activityId) : undefined));
  const id = ref?.activityId;
  useEffect(() => {
    if (ref) agentJobs.track(ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return job;
}

// The most recently submitted of `jobs` (by created_at; unknown sorts first).
export function newestJob(jobs: TrackedJob[]): TrackedJob | undefined {
  const t = (j: TrackedJob) => (j.createdAt ? Date.parse(j.createdAt) || 0 : 0);
  return jobs.reduce<TrackedJob | undefined>((best, j) => (!best || t(j) >= t(best) ? j : best), undefined);
}

export function useAgentJobs(): TrackedJob[] {
  return useSyncExternalStore(agentJobs.subscribe, agentJobs.all);
}

// ---- Estimates -----------------------------------------------------------

export type JobEstimate = {
  phase: 'queued' | 'rendering' | 'done' | 'failed';
  elapsedSec: number;
  // From the gateway's ETA, counted down locally since it was reported.
  remainingSec?: number;
  queuePosition?: number;
  // 0–95 when an ETA is known; undefined = indeterminate.
  progress?: number;
};

export function jobEstimate(job: TrackedJob, now = Date.now()): JobEstimate {
  const phase =
    job.status === 'completed' ? 'done' : job.status === 'failed' ? 'failed' : job.status === 'processing' ? 'rendering' : 'queued';
  const started = job.createdAt ? Date.parse(job.createdAt) : NaN;
  const elapsedSec = Number.isFinite(started) ? Math.max(0, Math.round((now - started) / 1000)) : 0;
  const out: JobEstimate = { phase, elapsedSec };
  if (phase === 'queued' && job.queuePosition) out.queuePosition = job.queuePosition;
  const anchor = job.etaUpdatedAt ? Date.parse(job.etaUpdatedAt) : NaN;
  // 0 is what the gateway reports when it has no estimate.
  if ((phase === 'queued' || phase === 'rendering') && job.etaSeconds && Number.isFinite(anchor)) {
    const remaining = Math.max(0, Math.round(job.etaSeconds - (now - anchor) / 1000));
    out.remainingSec = remaining;
    const total = elapsedSec + remaining;
    out.progress = total > 0 ? Math.max(2, Math.min(95, (elapsedSec / total) * 100)) : undefined;
  }
  return out;
}

export function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m >= 60) return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}
